// Client\client-vite\src\pages\Dealers\BdcCreatePage.tsx

import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createBdcPerson } from "@/Calls/callDealer";

// Validation schema
const BdcSchema = z.object({
    bdc_firstname: z.string().min(2, "First name required"),
    bdc_lastname: z.string().min(2, "Last name required"),
    bdc_email: z.string().email("Invalid email"),
    bdc_password: z.string().min(6, "Password must be at least 6 characters"),
    bdc_mobilephone_number: z.string().optional(),
    bdc_job_title: z.string().optional(),
});

type BdcForm = z.infer<typeof BdcSchema>;

export default function BdcCreatePage() {
    const form = useForm<BdcForm>({
        resolver: zodResolver(BdcSchema),
        defaultValues: {
            bdc_firstname: "",
            bdc_lastname: "",
            bdc_email: "",
            bdc_password: "",
            bdc_mobilephone_number: "",
            bdc_job_title: "",
        },
    });

    const [submitting, setSubmitting] = React.useState(false);

    async function onSubmit(values: BdcForm) {
        try {
            setSubmitting(true);
            await createBdcPerson(values);

            toast("✅ BDC Rep created", {
                description: `${values.bdc_firstname} ${values.bdc_lastname} has been added successfully.`,
                action: {
                    label: "View Staff",
                    onClick: () => window.location.assign("/dealer/staff"),
                },
            });

            form.reset();
        } catch (err) {
            console.error(err);
            const message =
                err instanceof Error
                    ? err.message
                    : "Unexpected error — please try again later.";

            toast("❌ Error creating BDC Rep", { description: message });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="container mx-auto max-w-2xl py-8">
            <Card>
                <CardHeader>
                    <CardTitle>Add New BDC Rep</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="bdc_firstname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>First Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Jane" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="bdc_lastname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Last Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Doe" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="bdc_email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="email" placeholder="email@dealer.com" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="bdc_password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Password</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="password" placeholder="********" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="bdc_mobilephone_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Mobile Phone</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="(555) 555-5555" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="bdc_job_title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Job Title</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="BDC Representative" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={submitting} className="w-full">
                                {submitting ? "Creating..." : "Create BDC Rep"}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
